import { useCallback, useEffect, useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FiHome, FiBook, FiCreditCard, FiBarChart2, FiPlusCircle, FiShoppingCart, FiGrid, FiShield, FiFileText, FiUsers, FiClock, FiMonitor, FiBriefcase, FiPrinter, FiAlertCircle, FiSettings, FiCode, FiChevronDown, FiImage, FiWifi, FiMail } from 'react-icons/fi';
import api from '../api/axios';
import styles from './DashboardSidebar.module.css';

function hasFeature(features, feature) {
  if (!features || features.length === 0) return false;
  return features.includes('all_features') || features.includes(feature);
}

/**
 * Dashboard sidebar navigation — items filtered by the user's plan.
 */
function DashboardSidebar({ onNavigate }) {
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  const planFeatures = user?.plan?.features || [];

  const isEmployee = user?.type === 'employee';
  const ownerFeatures = user?.owner_plan_features || [];
  const effectiveFeatures = isEmployee ? ownerFeatures : planFeatures;
  const canOrder = hasFeature(effectiveFeatures, 'online_ordering');
  const canTables = hasFeature(effectiveFeatures, 'all_features');
  const canEmployees = hasFeature(effectiveFeatures, 'employee_management');
  const canAade = hasFeature(effectiveFeatures, 'aade_greece');
  const canPrinterSettings = hasFeature(effectiveFeatures, 'online_ordering');
  const canThirdParty = hasFeature(effectiveFeatures, 'third_party_integrations')
    || hasFeature(effectiveFeatures, 'efood_integration');
  const locationsCount = Number(user?.locations_count || 0);
  const maxLocationsRaw = Number(user?.max_locations);
  const maxLocations = Number.isFinite(maxLocationsRaw) && maxLocationsRaw > 0 ? maxLocationsRaw : 1;
  const canCreateLocation = maxLocationsRaw === -1 || locationsCount < maxLocations;

  const [pendingOrders, setPendingOrders] = useState(0);
  const [openGroups, setOpenGroups] = useState({});
  const efoodMenuIcon = <img src="/images/Efood_logo.svg" alt="efood" className={styles.menuLogoIcon} />;
  const woltMenuIcon = <img src="/images/wolt-seeklogo.svg" alt="Wolt" className={styles.menuLogoIcon} />;

  const fetchPendingOrdersCount = useCallback(async () => {
    try {
      const [pendingRes, pendingOnlineRes] = await Promise.all([
        api.get('/v1/orders?status=pending&per_page=1&page=1'),
        api.get('/v1/orders?status=pending_online_payment&per_page=1&page=1'),
      ]);
      const pendingCount = Number(pendingRes?.data?.total || 0);
      const pendingOnlineCount = Number(pendingOnlineRes?.data?.total || 0);
      setPendingOrders(pendingCount + pendingOnlineCount);
    } catch (_) {
      // Keep sidebar stable if this request fails.
    }
  }, []);

  useEffect(() => {
    if (!canOrder) return;
    fetchPendingOrdersCount();
    const id = setInterval(fetchPendingOrdersCount, 20000);
    return () => clearInterval(id);
  }, [fetchPendingOrdersCount, canOrder]);

  const links = useMemo(() => {
    if (isEmployee) {
      const items = [
        { to: '/my-shift', icon: <FiClock />, label: 'My Shift' },
      ];
      if (canOrder) {
        items.push({ to: '/orders', icon: <FiShoppingCart />, label: 'Orders', badge: pendingOrders });
      }
      if (canTables) {
        items.push({ to: '/tables', icon: <FiGrid />, label: 'Seating Areas' });
      }
      return items;
    }

    const items = [
      { to: '/dashboard', icon: <FiHome />, label: 'Overview' },
      {
        group: true,
        icon: <FiBook />,
        label: 'Menus',
        children: [
          { to: '/menus', label: 'All Menus', end: true },
          { to: '/dishes', label: 'Dishes' },
          { to: '/categories', label: 'Categories' },
          { to: '/option-groups', label: 'Option Groups' },
        ],
      },
    ];

    if (canOrder) {
      items.push(
        {
          group: true,
          icon: <FiShoppingCart />,
          label: 'Orders',
          badge: pendingOrders,
          children: [
            { to: '/orders', label: 'All Orders', end: true },
            { to: '/orders/create', label: 'Create Order' },
            { to: '/orders/archived', label: 'Archived Orders' },
            { to: '/orders/exports', label: 'Exports' },
            { to: '/customers', label: 'Customers' },
          ],
        },
        { to: '/kds', icon: <FiMonitor />, label: 'Kitchen Display' },
      );
    }
    if (canTables) {
      items.push({ to: '/tables', icon: <FiGrid />, label: 'Seating Areas' });
    }

    if (canEmployees) {
      items.push(
        {
          group: true,
          icon: <FiUsers />,
          label: 'Employees & Duties',
          children: [
            { to: '/employees', label: 'Employees' },
            { to: '/employee-dashboard', label: 'Employee Stats' },
          ],
        },
      );
    }
    const settingsChildren = [];
    if (canAade) {
      settingsChildren.push(
        { to: '/aade', label: 'AADE / Billing', icon: <FiFileText />, end: true },
        { to: '/aade/billing-errors', label: 'AADE Billing Errors', icon: <FiAlertCircle /> },
      );
    }
    if (canPrinterSettings) {
      settingsChildren.push({ to: '/settings/printers', label: 'PrintNode Settings', icon: <FiPrinter /> });
    }
    settingsChildren.push({ to: '/company', label: 'Company Settings', icon: <FiBriefcase /> });
    if (canThirdParty) {
      settingsChildren.push({ to: '/settings/third-party', label: 'efood Settings', icon: efoodMenuIcon });
      settingsChildren.push({ to: '/settings/wolt', label: 'Wolt Settings', icon: woltMenuIcon });
    }
    if (canOrder) {
      settingsChildren.push({ to: '/settings/smtp', label: 'SMTP Settings', icon: <FiMail /> });
    }
    settingsChildren.push({ to: '/settings/wifi-qr', label: 'Wi-Fi QR Codes', icon: <FiWifi /> });
    settingsChildren.push({ to: '/widget', label: 'Widget', icon: <FiCode /> });
    settingsChildren.push({ to: '/media', label: 'Media', icon: <FiImage /> });
    if (settingsChildren.length > 0) {
      items.push({
        group: true,
        icon: <FiSettings />,
        label: 'Settings',
        children: settingsChildren,
      });
    }

    items.push({ to: '/analytics', icon: <FiBarChart2 />, label: 'Analytics' });
    if (canCreateLocation) {
      items.push({ to: '/onboarding', icon: <FiPlusCircle />, label: 'New Setup' });
    }
    items.push({ to: '/pricing', icon: <FiCreditCard />, label: 'Upgrade Plan' });

    if (user?.is_admin) {
      items.push({ to: '/admin', icon: <FiShield />, label: 'Admin Panel' });
    }

    return items;
  }, [canOrder, canTables, canEmployees, canAade, canPrinterSettings, canThirdParty, pendingOrders, user?.is_admin, isEmployee, ownerFeatures, canCreateLocation]);

  const isPathActive = useCallback((path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  }, [location.pathname]);

  useEffect(() => {
    const activeGroup = links.find((link) => link.group && link.children.some((child) => isPathActive(child.to)));
    if (!activeGroup) return;
    setOpenGroups((prev) => ({ ...prev, [activeGroup.label]: true }));
  }, [isPathActive, links]);

  const toggleGroup = (label) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <aside className={styles.sidebar}>
      <nav className={styles.nav}>
        {links.map((link) => (
          link.group ? (
            <div key={link.label} className={styles.group}>
              <button
                type="button"
                className={`${styles.groupHeader} ${openGroups[link.label] ? styles.groupHeaderOpen : ''}`}
                onClick={() => toggleGroup(link.label)}
                aria-expanded={openGroups[link.label] ? 'true' : 'false'}
              >
                <span className={styles.icon}>{link.icon}</span>
                <span className={styles.label}>{link.label}</span>
                {link.badge > 0 && (
                  <span className={styles.badge}>
                    {link.badge > 99 ? '99+' : link.badge}
                  </span>
                )}
                <span className={styles.groupChevron}><FiChevronDown /></span>
              </button>
              <div className={`${styles.groupChildren} ${openGroups[link.label] ? styles.groupChildrenOpen : ''}`}>
                {link.children.map((child) => (
                  <NavLink
                    key={child.to}
                    to={child.to}
                    end={!!child.end}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      `${styles.link} ${styles.childLink} ${isActive ? styles.active : ''}`
                    }
                  >
                    {child.icon ? <span className={styles.icon}>{child.icon}</span> : <span className={styles.childIconSpacer} />}
                    <span className={styles.label}>{child.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ) : (
            <NavLink
              key={link.to}
              to={link.to}
              onClick={onNavigate}
              className={({ isActive }) =>
                `${styles.link} ${isActive ? styles.active : ''}`
              }
              end={link.to === '/dashboard'}
            >
              <span className={styles.icon}>{link.icon}</span>
              <span className={styles.label}>{link.label}</span>
              {link.badge > 0 && (
                <span className={styles.badge}>
                  {link.badge > 99 ? '99+' : link.badge}
                </span>
              )}
            </NavLink>
          )
        ))}
      </nav>
    </aside>
  );
}

export default DashboardSidebar;
